/**
 * App Version Adoption (+35) — utilitários compartilhados.
 *
 * Lê os campos gravados pelo app do cliente em `users/{uid}`:
 *  - app_version (string, ex. "2.4.3+35")
 *  - app_build_number (number, ex. 35)
 *  - app_platform ("ios" | "android")
 *  - app_version_reported_at (Timestamp)
 *  - app_device_model (string, +37; iOS marketing name / Android manufacturer+model)
 *
 * Regras de negócio (ver docs/NEXT_APP_UPDATE_VERSION_2.4.3_BUILD_35.md):
 *  - Up to date: app_build_number >= build alvo da plataforma do cliente.
 *  - Outdated: tem app_build_number mas < alvo.
 *  - Unknown: sem app_build_number (nunca reportou / ainda não abriu +35).
 *  - user_type: 0 = Individual, 1 = Corporate.
 */

export const ADOPTION_STATUS = {
  UP_TO_DATE: "uptodate",
  OUTDATED: "outdated",
  UNKNOWN: "unknown",
};

/** Cores alinhadas com os chips/estados já usados no Admin. */
export const ADOPTION_STATUS_META = {
  [ADOPTION_STATUS.UP_TO_DATE]: { label: "Up to date", color: "#2af598" },
  [ADOPTION_STATUS.OUTDATED]: { label: "Outdated", color: "#ff9800" },
  [ADOPTION_STATUS.UNKNOWN]: { label: "Unknown", color: "#90a4ae" },
};

/** Converte o build de um usuário para number (ou null se ausente/inválido). */
const parseBuild = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Classifica um usuário em up to date / outdated / unknown comparando o build
 * reportado com o build alvo da plataforma do próprio usuário.
 */
export const classifyUser = (user, targetIos = 0, targetAndroid = 0) => {
  const build = parseBuild(user?.app_build_number);
  if (build === null) return ADOPTION_STATUS.UNKNOWN;

  const platform = String(user?.app_platform || "").toLowerCase();
  const target = platform === "ios" ? Number(targetIos) || 0 : Number(targetAndroid) || 0;

  // Sem alvo configurado: qualquer build reportado conta como up to date.
  if (!target) return ADOPTION_STATUS.UP_TO_DATE;

  return build >= target ? ADOPTION_STATUS.UP_TO_DATE : ADOPTION_STATUS.OUTDATED;
};

const emptyBucket = () => ({
  total: 0,
  [ADOPTION_STATUS.UP_TO_DATE]: 0,
  [ADOPTION_STATUS.OUTDATED]: 0,
  [ADOPTION_STATUS.UNKNOWN]: 0,
});

/**
 * Calcula as métricas de adoption a partir de uma lista de usuários (role === 1).
 * Retorna totais globais e breakdown Individual (user_type === 0) /
 * Corporate (user_type === 1).
 */
export const computeAdoptionStats = (users = [], targetIos = 0, targetAndroid = 0) => {
  const all = emptyBucket();
  const individual = emptyBucket();
  const corporate = emptyBucket();

  (users || []).forEach((user) => {
    const status = classifyUser(user, targetIos, targetAndroid);
    all.total += 1;
    all[status] += 1;

    if (user?.user_type === 0) {
      individual.total += 1;
      individual[status] += 1;
    } else if (user?.user_type === 1) {
      corporate.total += 1;
      corporate[status] += 1;
    }
  });

  return { all, individual, corporate, targetIos: Number(targetIos) || 0, targetAndroid: Number(targetAndroid) || 0 };
};

/** Normaliza um Timestamp/Date/number para Date (ou null). */
const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Formata o "Last reported" no mesmo estilo relativo do "Last Seen"
 * (Online / Xm ago / Xh ago / Xd ago / Xy ago / Never).
 */
export const formatReportedAt = (value) => {
  const date = toDate(value);
  if (!date) return "Never";

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 5) return "Online";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 365) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

/**
 * String curta da versão do app para tabela/perfil.
 * Prioriza app_version ("2.4.3+35"); cai para "build N"; senão fallback.
 */
export const formatAppVersion = (user, fallback = "Not reported yet") => {
  const version = String(user?.app_version || "").trim();
  if (version) return version;
  const build = parseBuild(user?.app_build_number);
  if (build !== null) return `build ${build}`;
  return fallback;
};

/** Label legível da plataforma. */
export const formatPlatform = (user) => {
  const platform = String(user?.app_platform || "").toLowerCase();
  if (platform === "ios") return "iOS";
  if (platform === "android") return "Android";
  return "--";
};

/** Modelo do aparelho quando o +37 já reportou; senão a plataforma. */
export const formatDevice = (user) => {
  const model = String(user?.app_device_model || "").trim();
  if (model) return model;
  return formatPlatform(user);
};
